import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { WEBSITE_STATUS_OPTIONS, type LeadFormData } from "../types";

interface Props {
  data: LeadFormData;
  onChange: (patch: Partial<LeadFormData>) => void;
}

export function StepBusiness({ data, onChange }: Props) {
  const status = data.websiteStatus;
  const hasExisting = status === "needs-replacing" || status === "terrible";

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">
          <Globe className="h-4 w-4" />
          Your Business
        </div>
        <h2 className="text-balance text-3xl font-bold tracking-tighter text-foreground sm:text-4xl">
          Tell us about your business & web presence
        </h2>
        <p className="text-base text-muted-foreground">
          We need to know what we're working with.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-foreground">
          Business Name <span className="text-brand-blue">*</span>
        </label>
        <Input
          value={data.businessName ?? ""}
          onChange={(e) => onChange({ businessName: e.target.value })}
          placeholder="e.g. Marco's Kitchen"
          maxLength={100}
          className="h-12 rounded-xl"
        />
      </div>

      <div className="space-y-3">
        <label className="text-sm font-semibold text-foreground">
          Do you have a website currently? <span className="text-brand-blue">*</span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {WEBSITE_STATUS_OPTIONS.map((opt) => {
            const selected = status === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  onChange({
                    websiteStatus: opt.value,
                    currentWebsiteUrl:
                      opt.value === "starting-fresh" ? undefined : data.currentWebsiteUrl,
                  })
                }
                className={cn(
                  "rounded-xl border bg-background px-4 py-3 text-sm font-medium text-gray-950 transition-all",
                  "hover:border-brand-blue hover:shadow-sm",
                  selected
                    ? "border-brand-blue bg-brand-blue/5 ring-2 ring-brand-blue/30"
                    : "border-border",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {hasExisting && (
        <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-5">
          <label className="text-sm font-semibold text-foreground">
            Current website URL <span className="text-brand-blue">*</span>
          </label>
          <Input
            type="url"
            value={data.currentWebsiteUrl ?? ""}
            onChange={(e) => onChange({ currentWebsiteUrl: e.target.value })}
            placeholder="https://www.yourbusiness.com"
            maxLength={255}
            className="h-12 rounded-xl"
          />
          <p className="text-xs text-muted-foreground">
            We'll review your current site to understand what to improve.
          </p>
        </div>
      )}

      {status && (
        <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-5">
          <label className="text-sm font-semibold text-foreground">
            Got a website you love the look of?{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Input
            type="url"
            value={data.inspirationUrl ?? ""}
            onChange={(e) => onChange({ inspirationUrl: e.target.value })}
            placeholder="https://www.a-site-you-admire.com"
            maxLength={255}
            className="h-12 rounded-xl"
          />
          <p className="text-xs text-muted-foreground">
            Share a site that inspires you — helps us nail your style.
          </p>
        </div>
      )}
    </div>
  );
}
